import React, { FC, ReactElement, useEffect } from 'react';
import { StyleProp, Text, TextStyle, View, ViewStyle } from 'react-native';
import i18n from 'i18n-js';
import moment from 'moment';

import { scale as initialScale } from '@components/frame';

import {
  subStatusName,
  machineStatus as machineStatusConst,
  machineStatusColorName,
  machineStatusName,
  convertTime,
  isPhone,
} from '../../../../../../helpers';
import { font as fonts, FontStyles, font as initialFont } from '@styles/fonts';
import { ItemWrapperProps } from '@v2/pages/machines/common/types/props';
import { Machine } from '@interfaces/machines';
import { ColorFunction } from '@styles/colors/types';
import { useForceUpdateEverySecond } from '@v2/pages/common/hooks/useForceUpdateEverySecond';

interface TimeLineProps extends Pick<Machine, 'machineStatus'>, Pick<ItemWrapperProps, 'selected'> {
  font?: FontStyles;
  scale?: (n: any) => any;
  hideNCFile?: boolean;
  hideDurationTime?: boolean;
  hideStatusName?: boolean;
  baseTimeColor?: string;
  timeLineStyle?: StyleProp<ViewStyle>;
  timelineHeight?: number;
  timelineBorderRadius?: number;
  timeMarginTop?: number;
  targetMarkHeight?: number;
  targetMarkTop?: number;
  getTimeLineWrapper?: (children: ReactElement) => ReactElement;
  getTimeDisplayComponent?: (timeValue: number) => ReactElement;
  timeOffset: number;
  C: ColorFunction;
  layoutForPhone?: boolean;
  timelineFilledColor?: string;
  timelineNotFilledColor?: string;
  customStatusText?: string;
  customStatusTextAlreadyTranslated?: boolean;
  withoutSlashBetweenRunningAndTargetTime?: boolean;
  separateTargetDuration?: boolean;
  timeFontType?: keyof typeof fonts;
  statusFontType?: keyof typeof fonts;
  statusColor?: string;
  timeColor?: string;
  currentTimeStyles?: StyleProp<TextStyle>;
  targetTimeStyles?: StyleProp<ViewStyle>;
  statusBlockStyles?: StyleProp<ViewStyle>;
  containerStyles?: StyleProp<ViewStyle>;
  setActualTimeForLatestEvent?: (time: number) => void;
}

interface TargetDurationDisplayProps {
  showSlash: boolean;
  color: string;
  time: string;
  font: FontStyles;
  fontType: keyof typeof fonts;
  opacity?: number;
  targetTimeStyles?: StyleProp<ViewStyle>;
}

const TargetDurationDisplay: FC<TargetDurationDisplayProps> = ({ showSlash, color, time, font, fontType, opacity, targetTimeStyles }) => (
  <View style={[{ flexDirection: 'row' }, targetTimeStyles]}>
    {showSlash && <Text style={[font[fontType], { color, opacity }]}> / </Text>}
    <Text style={[font[fontType], { color, opacity }]}>{time}</Text>
  </View>
);

export default React.memo(
  ({
    timeOffset,
    C,
    machineStatus,
    font = initialFont,
    scale = initialScale,
    hideNCFile,
    selected,
    hideDurationTime,
    hideStatusName,
    baseTimeColor = 'Label/Light/Tertiary',
    timelineHeight = 2,
    timelineBorderRadius = 2,
    timeMarginTop = 8,
    targetMarkHeight = 8,
    targetMarkTop = -3,
    getTimeLineWrapper,
    getTimeDisplayComponent,
    timeLineStyle,
    layoutForPhone,
    timelineFilledColor,
    timelineNotFilledColor,
    customStatusText,
    customStatusTextAlreadyTranslated,
    withoutSlashBetweenRunningAndTargetTime,
    separateTargetDuration,
    timeFontType = 'Time 2',
    statusFontType = 'Subhead 2',
    timeColor,
    statusColor,
    currentTimeStyles,
    targetTimeStyles,
    statusBlockStyles,
    containerStyles,
    setActualTimeForLatestEvent,
  }: TimeLineProps) => {
    const { lastUpdatedOn, targetDuration, status, ncFile } = machineStatus ?? {};

    useForceUpdateEverySecond();

    const time = moment.utc().add(timeOffset, 'milliseconds');

    const runningTime = time.diff(lastUpdatedOn ? moment.utc(lastUpdatedOn) : moment.utc(), 's');

    const isTargetDuration = Boolean(targetDuration);
    const showTargetDurationMark = runningTime > targetDuration && isTargetDuration;

    useEffect(() => {
      setActualTimeForLatestEvent?.(runningTime);
    }, [runningTime]);

    const persentageProgress = Math.min(100, showTargetDurationMark ? 100 : (runningTime * 100) / targetDuration);
    const targetDurationPosition = (targetDuration * 100) / runningTime;

    const subStatus = machineStatus?.subStatus as keyof typeof subStatusName & keyof typeof machineStatusName;

    const statusText = i18n.t(machineStatus?.status === machineStatusConst.NOTPRODUCTIVE ? subStatusName[subStatus] : machineStatusName[subStatus]);

    const TargetTimeComponent = (
      <TargetDurationDisplay
        showSlash={!withoutSlashBetweenRunningAndTargetTime}
        color={timeColor ?? C('Label/Light/Quinary')}
        time={convertTime(targetDuration)}
        font={font}
        fontType={timeFontType}
        opacity={separateTargetDuration && !targetDuration ? 0 : 1}
        targetTimeStyles={targetTimeStyles}
      />
    );

    const timelineTextContent = (
      <View
        style={[
          {
            ...(getTimeDisplayComponent && (isPhone || layoutForPhone) ? {} : { width: '100%' }),
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginTop: scale(timeMarginTop),
            alignItems: 'center',
            alignContent: 'center',
          },
          containerStyles,
        ]}
      >
        {getTimeDisplayComponent && (
          <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
            {
              <>
                {getTimeDisplayComponent(runningTime)}
                {isTargetDuration && !hideDurationTime && (
                  <>
                    {!withoutSlashBetweenRunningAndTargetTime && (
                      <Text style={[font[timeFontType], { color: timeColor ?? C('Label/Light/Quinary') }]}> / </Text>
                    )}
                    {getTimeDisplayComponent(targetDuration)}
                  </>
                )}
              </>
            }
          </View>
        )}

        {!getTimeDisplayComponent && (
          <Text
            style={[
              {
                color: timeColor ?? C(selected ? 'Label/Light/AlwaysLight Secondary' : baseTimeColor),
                fontVariant: ['tabular-nums'],
              },
              font[timeFontType ?? 'Body'],
              currentTimeStyles,
            ]}
          >
            {convertTime(runningTime)}

            {!separateTargetDuration && isTargetDuration && !hideDurationTime && TargetTimeComponent}
          </Text>
        )}

        {!hideStatusName && (
          <Text
            style={[
              {
                color: statusColor ?? C(selected ? 'Label/Light/AlwaysLight Secondary' : baseTimeColor),
                textAlign: 'center',
                marginLeft: scale(5),
                marginTop: scale(2),
              },
              font[statusFontType],
              statusBlockStyles,
            ]}
          >
            {customStatusText ? (customStatusTextAlreadyTranslated ? customStatusText : i18n.t(customStatusText)) : statusText}
          </Text>
        )}

        {separateTargetDuration && !hideDurationTime && TargetTimeComponent}
      </View>
    );

    return (
      <View style={{ width: '100%', flexDirection: 'row' }}>
        <View style={{ width: '100%' }}>
          {!hideNCFile && (
            <View style={{ flexDirection: 'row' }}>
              <View style={{ marginTop: scale(9.5), marginBottom: scale(10.5) }}>
                <Text style={[font.Body, { color: C('Label/Light/Primary') }]} numberOfLines={1} ellipsizeMode={'middle'}>{`${ncFile}`}</Text>
              </View>
            </View>
          )}
          <View style={[{ width: '100%' }, timeLineStyle]}>
            <View
              style={{
                backgroundColor: timelineNotFilledColor ?? C('Background/Light/Chart'),
                width: '100%',
                height: scale(timelineHeight),
                borderRadius: scale(timelineBorderRadius),
              }}
            >
              {isTargetDuration && (
                <View
                  style={{
                    backgroundColor: timelineFilledColor ?? C(machineStatusColorName[status]),
                    width: `${persentageProgress}%`,
                    height: scale(timelineHeight),
                    borderRadius: scale(timelineBorderRadius),
                  }}
                />
              )}
              {showTargetDurationMark && (
                <View
                  style={{
                    position: 'absolute',
                    width: scale(2),
                    height: scale(targetMarkHeight),
                    left: `${targetDurationPosition}%`,
                    top: scale(targetMarkTop),
                    borderRadius: scale(1),
                    backgroundColor: C('Chart/Light/Warning'),
                  }}
                />
              )}
            </View>
          </View>
          {!getTimeLineWrapper ? timelineTextContent : getTimeLineWrapper(timelineTextContent)}
        </View>
      </View>
    );
  },
  (prev, next) =>
    prev.timeOffset === next.timeOffset &&
    prev.machineStatus?.lastUpdatedOn === next.machineStatus?.lastUpdatedOn &&
    prev.machineStatus?.targetDuration === next.machineStatus?.targetDuration &&
    prev.machineStatus === next.machineStatus &&
    prev.machineStatus?.ncFile === next.machineStatus?.ncFile &&
    prev.font === next.font &&
    prev.selected === next.selected &&
    prev.C.scheme === next.C.scheme &&
    prev.layoutForPhone === next.layoutForPhone &&
    prev.timelineFilledColor === next.timelineFilledColor &&
    prev.timelineNotFilledColor === next.timelineNotFilledColor &&
    prev.customStatusText === next.customStatusText &&
    prev.customStatusTextAlreadyTranslated === next.customStatusTextAlreadyTranslated &&
    prev.withoutSlashBetweenRunningAndTargetTime === next.withoutSlashBetweenRunningAndTargetTime &&
    prev.separateTargetDuration === next.separateTargetDuration &&
    prev.timeFontType === next.timeFontType &&
    prev.timeColor === next.timeColor &&
    prev.statusColor === next.statusColor &&
    prev.currentTimeStyles === next.currentTimeStyles &&
    prev.targetTimeStyles === next.targetTimeStyles &&
    prev.statusBlockStyles === next.statusBlockStyles &&
    prev.containerStyles === next.containerStyles &&
    prev.targetMarkTop === next.targetMarkTop &&
    prev.targetMarkHeight === next.targetMarkHeight
);
